import type {
  InfiniteQueryObserverOptions,
  QueryObserverOptions,
} from '@tanstack/query-core';

export type Resource<
  Path extends readonly string[] = readonly string[],
  Params extends Record<string, any> | undefined = undefined,
  Body extends Record<string, any> | undefined = undefined,
> = {
  path: Path;
  method: 'GET' | 'POST';
  body?: Body;
  params?: Params;
  infinite: boolean;
  scope?: string[];
};

export type ResourceOf<
  T extends
    | QueryObserverOptions<any, any, any, any, any>
    | InfiniteQueryObserverOptions<any, any, any, any, any, any>,
> =
  T extends QueryObserverOptions<
    infer _TQueryFnData,
    infer _TError,
    infer _TData,
    infer _TQueryData,
    infer TQueryKey
  >
    ? TQueryKey
    : T extends InfiniteQueryObserverOptions<
          infer _TQueryFnData,
          infer _TError,
          infer _TData,
          infer _TQueryData,
          infer TQueryKey,
          infer _TPageParam
        >
      ? TQueryKey
      : never;

export const getResource = <
  Path extends readonly string[] = readonly string[],
  Params extends Record<string, any> | undefined = undefined,
  Body extends Record<string, any> | undefined = undefined,
>(
  path: Path,
  opts: Partial<Omit<Resource<Path, Params, Body>, 'path'>> = {}
): Resource<Path, Params, Body> => {
  const { method = 'GET', infinite = false, body, params, scope } = opts;

  const resource: Resource<Path, Params, Body> = { path, method, infinite };

  if (body) {
    resource.body = body;
  }

  if (params) {
    resource.params = params;
  }

  if (scope) {
    resource.scope = scope;
  }

  return resource;
};
